import React, { useCallback } from 'react'
import { Link } from 'react-router-dom'

import { H2, H4, Span, H3 } from '../../lib/components'
import { OpportunityCard } from '../../lib/components/OpportunityCard'
import { calculateCommittedPercentage, trimText } from '../../lib/utils/helpers'
import { OpportunityCardProps, RawOpportunity } from '../../lib/types'
import { LearnMoreSection } from './learn-more-section'
import {
  InvestIconHIW,
  EarnIconHIW,
  RedeemIconHIW,
  BackedByGlobalInstituitions,
  LicensedByExperts,
  AssetSecurity,
} from '../../lib/components/Icon'
import './home.scss'
import { Icon } from '@mui/material'

const howWorks = [
  {
    title: 'Sign Up',
    icon: InvestIconHIW,
    subTitle: 'Create a free account & complete your KYC',
  },
  {
    title: 'Evaluate & Invest',
    icon: EarnIconHIW,
    subTitle:
      'Go through the opportunities & choose the one that best fits into your existing portfolio. Transfer funds & request allocation through Credibila once KYC is approved',
    tooltipText:
      'Clients should note that all investments are subject to risk. You are advised to consult your financial advisor to help evaluate the opportunity before investing.',
  },
  {
    title: 'Track & Review',
    icon: RedeemIconHIW,
    subTitle: "Have access to periodic reports to view your portfolio's composition & performance",
  },
]

interface HomePresenterProps {
  handleButtonClick: () => void
  expand: boolean
  handleProjectClick: (projectSlug: string, id: number) => void
  projects: RawOpportunity[]
  opportunityLoading: boolean
}

const HomePresenter = React.forwardRef<HTMLDivElement, HomePresenterProps>(
  ({ handleProjectClick, projects, opportunityLoading }) => {
    const scrollToHowItWorks = useCallback(() => {
      const howItWorksSection = document.querySelector('.how-it-works-wrapper')
      if (howItWorksSection) {
        const yOffset = -100
        const y = howItWorksSection.getBoundingClientRect().top + window.pageYOffset + yOffset
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    }, [])

    return (
      <div className='home-page'>
        {/* Hero Section */}
        <section className='hero-section'>
          <div className='hero-container'>
            <div className='hero-content'>
              <h1 className='hero-title'>Invest in What Moves the World</h1>
              <p className='hero-subtitle'>
                Curated access to private market opportunities from startups to real estate, venture
                debt, and beyond.
              </p>
              <p className='hero-detail'>Start building your portfolio from just US $1,000</p>
            </div>
            <div className='hero-image'>
              <img
                src='https://credibila-assets.s3.amazonaws.com/images/home-hero-globe.png'
                alt='globe'
              />
            </div>
          </div>
          <div className='hero-actions'>
            <Link to='/opportunities' className='btn-primary' data-testid='homepage-explore-link'>
              Explore Opportunities
            </Link>
            <button onClick={scrollToHowItWorks} className='btn-link'>
              See How It Works →
            </button>
          </div>
        </section>

        {/* Live Opportunities Section */}
        <section className='opportunities-section'>
          <div className='section-header'>
            <H2 text='Opportunities' classes={['section-title']} />
            <Span
              variant='thin'
              classes={['section-subtitle']}
              text='Explore curated investments across asset classes.'
            />
          </div>

          <div className='opportunities-container' data-testid='opportunity-card-container'>
            {opportunityLoading ? (
              <div className='loading-state'>
                <Span variant='thin' text='Loading Opportunities..' />
              </div>
            ) : projects.length > 0 ? (
              <div className='opportunities-grid'>
                {projects.map(
                  ({
                    id,
                    opportunity_title,
                    opportunity_slug,
                    closing_date,
                    opportunity_summary,
                    total_investment_amount,
                    raise_goal,
                    images,
                    asset_class,
                    ticket_size,
                    currency_code,
                    cover_image,
                    short_opportunity_summary,
                    asset_class_data,
                  }: OpportunityCardProps) => (
                    <OpportunityCard
                      key={id}
                      id={id}
                      projectSlug={opportunity_slug}
                      closingDate={closing_date}
                      projectName={opportunity_title}
                      projectSummary={
                        short_opportunity_summary ?? trimText(opportunity_summary, 100)
                      }
                      committedPercentage={
                        total_investment_amount
                          ? calculateCommittedPercentage(
                              String(total_investment_amount) || '',
                              String(raise_goal) || '',
                              asset_class,
                              asset_class_data,
                            )
                          : '0%'
                      }
                      irr={undefined}
                      projectCover={cover_image ?? images[0]}
                      assetClass={asset_class}
                      ticketSize={String(ticket_size)}
                      handleClick={handleProjectClick}
                      currencyCode={currency_code}
                      dataTestId={`opportunity-card-${id}`}
                    />
                  ),
                )}
              </div>
            ) : (
              <div className='empty-state'>
                <Span variant='thin' text='No Opportunities Found' />
              </div>
            )}
          </div>
        </section>

        {/* How it Works Section */}
        <section className='how-it-works-wrapper'>
          <div className='section-header'>
            <H2 text='How it Works?' classes={['section-title']} />
          </div>
          <div className='how-it-works-grid'>
            {howWorks.map(({ title, subTitle, icon }) => {
              const parts = subTitle.split('*')
              return (
                <div key={title} className='how-it-works-card'>
                  <div className='card-icon'>
                    <Icon component={icon} sx={{ fontSize: 80 }} />
                  </div>
                  <H3 text={title} classes={['card-title']} />
                  <div className='card-description-wrapper'>
                    <Span text={subTitle} classes={['card-description']} />
                  </div>
                </div>
              )
            })}
          </div>
        </section>

        {/* Why Clients Choose Section */}
        <section className='why-choose-section'>
          <div className='section-header'>
            <H2 text='Why Clients Choose Credibila?' classes={['section-title']} />
            <H4
              text='Designed for serious clients, built by experts.'
              classes={['section-subtitle-large']}
            />
          </div>
          <div className='why-choose-container'>
            <div className='why-choose-content'>
              <div className='features-grid'>
                <div className='feature-card'>
                  <H4 text='Multi-Asset Access' classes={['feature-title']} />
                  <p className='basic feature-description'>
                    Diversify across private equity, venture debt, trade finance, and more.
                  </p>
                </div>

                <div className='feature-card'>
                  <H4 text='Institutional Due Diligence' classes={['feature-title']} />
                  <p className='basic feature-description'>
                    Every deal undergoes multi-layer evaluation by Advisory Partner.
                  </p>
                </div>

                <div className='feature-card'>
                  <H4 text='Seamless Investing' classes={['feature-title']} />
                  <p className='basic feature-description'>
                    Invest, track, and manage payouts securely in one place.
                  </p>
                </div>

                <div className='feature-card'>
                  <H4 text='Low Minimums, Global Reach' classes={['feature-title']} />
                  <p className='basic feature-description'>
                    Start with US $1,000 and co-invest alongside global institutions.
                  </p>
                </div>
              </div>
            </div>

            <div className='why-choose-image'>
              <img
                src='https://credibila-assets.s3.amazonaws.com/images/wc-hero.png'
                alt='why choose credibila'
              />
            </div>
          </div>
        </section>

        {/* Regulation Section */}
        <section className='regulation-section'>
          <div className='regulation-container'>
            <div className='regulation-header'>
              <H2 text='Your Investments, Protected by Regulation' classes={['section-title']} />
            </div>

            <div className='regulation-cards'>
              <div className='regulation-card'>
                <div className='regulation-badge'>
                  <BackedByGlobalInstituitions />
                </div>
                <p className='regulation-text'>Backed by trusted global institutions</p>
              </div>

              <div className='regulation-card'>
                <div className='regulation-badge'>
                  <LicensedByExperts />
                </div>
                <p className='regulation-text'>Licensed & regulated by experts</p>
              </div>

              <div className='regulation-card'>
                <div className='regulation-badge'>
                  <AssetSecurity />
                </div>
                <p className='regulation-text'>Assets securely held in segregation</p>
              </div>
            </div>
          </div>
        </section>

        {/* Learn More Section */}
        <section className='learn-more-section'>
          <div className='learn-more-container'>
            <H2 text='Learn More' classes={['section-title']} />
            <LearnMoreSection />
          </div>
        </section>
      </div>
    )
  },
)

HomePresenter.displayName = 'HomePresenter'
export { HomePresenter }
